
import { defineComponent, ref, computed } from 'vue';
import { useRouter, RouteRecordRaw } from 'vue-router';
import MaterialIcon from '@/components/MaterialIcon.vue';
import AppNavigationSidebar from '@/components/AppNavigationSidebar.vue';
import AppNavigationRouterLink from '@/components/AppNavigationRouterLink.vue';

export default defineComponent({
  name: 'AppNavigation',

  components: {
    MaterialIcon,
    AppNavigationSidebar,
    AppNavigationRouterLink
  },

  setup() {
    const isOpen = ref(false);

    const closeNavigation = () => {
      isOpen.value = false;
    };

    const toggleNavigation = () => {
      isOpen.value = !isOpen.value;
    };

    const router = useRouter();
    const routes = computed(() => router.options.routes as RouteRecordRaw[]);
    const allProjectRoutes = computed(() => routes.value.filter(route => route?.meta?.isProject));
    const landingRoute = computed(() => routes.value.find(route => route?.meta?.isLanding));

    const sortRouteRecordsByCreatedOn = (records: RouteRecordRaw[]) => {
      if (!records.length) return records;

      return records.slice().sort((a, b) => {
        if (!b.meta || !a.meta) return 0;

        return b.meta.createdOn.getTime() - a.meta.createdOn.getTime();
      });
    };

    type AggregatedProjectRoutes = {
      [key: number]: RouteRecordRaw[];
    };

    const aggregatedProjectRoutes = allProjectRoutes.value.reduce(
      (accumulator: AggregatedProjectRoutes, route: RouteRecordRaw) => {
        if (!route.meta) return accumulator;
        const year: number = route.meta.createdOn.getFullYear();

        return {
          ...accumulator,
          // Spreads already aggregated routes from a given year into an array,
          // then adds the current route to the same array.
          [year]: [...(accumulator?.[year]?.length ? accumulator[year] : []), route]
        } as AggregatedProjectRoutes;
      },
      {}
    );

    const aggregatedProjectRoutesByCreatedOn = computed(() => {
      return Object.entries(aggregatedProjectRoutes)
        .map(
          ([key, value]) => [key, sortRouteRecordsByCreatedOn(value)] as [string, RouteRecordRaw[]]
        )
        .reverse();
    });

    return {
      isOpen,
      allProjectRoutes,
      landingRoute,
      aggregatedProjectRoutesByCreatedOn,
      closeNavigation,
      toggleNavigation
    };
  }
});
